import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Calendar, Copy, Check, ExternalLink } from "lucide-react";

/**
 * A "Sync to your calendar" card that surfaces the clinic's ICS feed URL.
 * Rendered identically for managers and sonographers — the backend returns
 * the same URL for both.
 */
export default function ClinicCalendarConnect() {
  const [url, setUrl] = useState("");
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/clinic/ics-url");
        setUrl(data?.ics_url || "");
      } catch (e) {
        setErr(e.response?.data?.detail || "Unable to load calendar link");
      }
    })();
  }, []);

  const copy = async () => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  if (err) return null;

  return (
    <div className="card-elev" data-testid="calendar-connect">
      <div className="flex items-start gap-4">
        <span className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--gold-light)" }}>
          <Calendar className="w-5 h-5" style={{ color: "var(--gold-hover)" }} />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold">Sync to your work calendar</h3>
          <p className="text-sm text-zinc-500 mt-1">
            Subscribe once — every scheduled clinic stretch appears in your calendar and updates automatically.
          </p>

          <div className="mt-4 flex items-stretch gap-2">
            <input
              readOnly
              value={url || "Loading…"}
              onFocus={(e) => e.target.select()}
              className="flex-1 h-10 px-3 rounded-md border border-zinc-200 bg-zinc-50 text-xs text-zinc-700 truncate"
              data-testid="calendar-connect-url"
            />
            <button
              type="button"
              onClick={copy}
              className="btn-outline h-10 px-3 text-sm shrink-0"
              disabled={!url}
              data-testid="calendar-connect-copy"
            >
              {copied ? <><Check className="w-4 h-4 mr-1" />Copied</> : <><Copy className="w-4 h-4 mr-1" />Copy link</>}
            </button>
          </div>

          <div className="mt-4 grid sm:grid-cols-3 gap-2">
            <a
              href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-zinc-600 hover:text-zinc-900 flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-200"
              data-testid="calendar-connect-google"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Add to Google Calendar
            </a>
            <a
              href="https://outlook.office.com/calendar/addcalendar"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-zinc-600 hover:text-zinc-900 flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-200"
              data-testid="calendar-connect-outlook"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Add to Outlook
            </a>
            <a
              href={url ? url.replace(/^https?:/, "webcal:") : "#"}
              className="text-xs text-zinc-600 hover:text-zinc-900 flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-200"
              data-testid="calendar-connect-apple"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Add to Apple Calendar
            </a>
          </div>
          <p className="text-[11px] text-zinc-400 mt-3">
            In Google Calendar choose <strong>From URL</strong> and paste the link above. Outlook: <strong>Add calendar → Subscribe from web</strong>. Apple: <strong>File → New Calendar Subscription</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}
